import { useEffect, useState } from "react";
import BondaWalletService from '../../../services/bondaWallet_service';
import ConversionFactorService from '../../../services/conversionFactor_service';
import CountryService from '../../../services/country_service';
import ExchangeHistoryService from '../../../services/exchangeHistory_service';
import ExchangeInteractionService from '../../../services/exchangeInteraction_service';
import ExchangeProductService from '../../../services/exchangeProduct_service';
import FileService from '../../../services/file_service';
import ScoreService from '../../../services/score_service';

const PrizesExchange = (props) => {
    const [prizes, setPrizes] = useState([]);
    const [premioSeleccionado, setPremioSeleccionado] = useState(null);
    const [userScore, setUserScore] = useState(0);
    const [country, setCountry] = useState('');
    const [aceptarTerminos, setAceptarTerminos] = useState(false);
    const [showInteractionSelection, setShowInteractionSelection] = useState(false);
    const [availableInteractions, setAvailableInteractions] = useState([]);
    const [conversionFactors, setConversionFactors] = useState([]);
    const [interactionQuantities, setInteractionQuantities] = useState({});
    const [bondaCurrencyTotal, setBondaCurrencyTotal] = useState(null);
    const [assigningBondaCurrency, setAssigningBondaCurrency] = useState(false);
    const [loadingInteractions, setLoadingInteractions] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const data = await CountryService.get(props.user?.FK_Country);
                if (props.logout(data)) return;
                setCountry(data?.Name);
            } catch(e) {
                props.message(e?.message || e, "error");
            }
        })();
    }, [props.user?.FK_Country]);

    useEffect(() => {
        (async () => {
            try {
                const data = await ScoreService.get(props.user?.Id);
                if (props.logout(data)) return;
                setUserScore(Number(data?.Score || 0));
            } catch(e) {
                props.message(e?.message || e, "error");
            }
        })();
    }, [props.user?.Id]);

    useEffect(() => {
        (async () => {
            try {
                const data = await ExchangeProductService.getAll();
                if (props.logout(data)) return;
                setPrizes(data || []);
            } catch(e) {
                props.message(e?.message || e, "error");
            }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const data = await ExchangeInteractionService.getAvailable();
                if (props.logout(data)) return;
                setAvailableInteractions(data || []);
                setInteractionQuantities({});
            } catch(e) {
                props.message(e?.message || e, "error");
            }
        })();
    }, []);

    const handlePremioSelect = (premio) => {
        if (isPrizeUnavailable(premio)) {
            props.message(redeemedPrizeMessage, 'error');
            return;
        }
        setPremioSeleccionado((current) => current?.id === premio.id ? null : premio);
    };

    const isBondaPrize = (premio = premioSeleccionado) => {
        const title = (premio?.title || '').toLowerCase();
        return title.includes('bonda');
    };

    const isUcTripPrize = (premio = premioSeleccionado) => {
        const title = (premio?.title || '').toLowerCase();
        return title.includes('viaje') || title.includes('uc');
    };

    const redeemedPrizeMessage = 'Producto ya canjeado, solo se puede canjear una vez por periodo';

    const isPrizeUnavailable = (premio = premioSeleccionado) => (
        isUcTripPrize(premio) && Boolean(premio?.redeemed)
    );

    const selectedInteractionsTotal = availableInteractions.reduce((total, interaction) => {
        const quantity = Number(interactionQuantities?.[interaction.id] || 0);
        const value = Number(interaction?.value || 0);
        return total + (quantity * value);
    }, 0);

    // Menor total alcanzable >= prizeValue combinando las interacciones disponibles
    const getMinimumCoveringScore = (prizeValue) => {
        const items = availableInteractions
            .map((interaction) => ({
                value: Number(interaction?.value || 0),
                available: Number(interaction?.itemsToExchange || 0),
            }))
            .filter((item) => item.value > 0 && item.available > 0);
        if (!items.length) return null;
        const limit = prizeValue + Math.max(...items.map((item) => item.value));
        let reachable = new Array(limit + 1).fill(false);
        reachable[0] = true;
        items.forEach(({ value, available }) => {
            const next = [...reachable];
            for (let sum = 0; sum <= limit; sum++) {
                if (!reachable[sum]) continue;
                for (let count = 1; count <= available && sum + count * value <= limit; count++) {
                    next[sum + count * value] = true;
                }
            }
            reachable = next;
        });
        for (let sum = prizeValue; sum <= limit; sum++) {
            if (reachable[sum]) return sum;
        }
        return null;
    };

    // Valida la selección contra el premio si el producto tiene valor visible (no aplica a Bonda)
    const getSelectionError = (premio = premioSeleccionado) => {
        if (!premio?.visibleScore || isBondaPrize(premio)) return null;
        const prizeValue = Number(premio.prize || 0);
        if (prizeValue <= 0 || selectedInteractionsTotal <= 0) return null;
        if (selectedInteractionsTotal < prizeValue) {
            return `Necesitas ${prizeValue - selectedInteractionsTotal} puntos más para canjear este producto`;
        }
        // Interacciones sin valor seleccionadas se perderían sin aportar puntos
        const hasZeroValueSelected = availableInteractions.some((interaction) => (
            Number(interactionQuantities?.[interaction.id] || 0) > 0 && Number(interaction?.value || 0) <= 0
        ));
        const minimumScore = getMinimumCoveringScore(prizeValue);
        if (hasZeroValueSelected || (minimumScore !== null && selectedInteractionsTotal > minimumScore)) {
            return `Este producto vale ${prizeValue} puntos y puedes canjearlo usando ${minimumScore} puntos. Ajusta tu selección para no perder puntos`;
        }
        return null;
    };

    const selectionError = getSelectionError();

    // Puntos que sobran del canje y no se conservan (no aplica a Bonda)
    const getLeftoverScore = (premio = premioSeleccionado) => {
        if (!premio?.visibleScore || isBondaPrize(premio) || selectionError) return 0;
        const prizeValue = Number(premio.prize || 0);
        if (prizeValue <= 0) return 0;
        return Math.max(selectedInteractionsTotal - prizeValue, 0);
    };

    const leftoverScore = getLeftoverScore();

    const hasEnoughPoints =(premio) => Number(userScore) >= Number(premio?.prize || 0);

    const canSubmit = () => aceptarTerminos;

    const loadAvailableInteractions = async () => {
        setLoadingInteractions(true);
        try {
            const [interactions, factors] = await Promise.all([
                ExchangeInteractionService.getAvailable(),
                isBondaPrize() ? ConversionFactorService.getAll() : Promise.resolve([]),
            ]);
            if (props.logout(interactions) || props.logout(factors)) return;
            setAvailableInteractions(interactions || []);
            setConversionFactors(factors || []);
            setInteractionQuantities({});
            setBondaCurrencyTotal(null);
        } catch(e) {
            props.message(e?.message || e, "error");
        } finally {
            setLoadingInteractions(false);
        }
    };

    const setInteractionQuantity = (interaction, value) => {
        const max = Number(interaction?.itemsToExchange || 0);
        const normalizedValue = String(value ?? '').replace(/\D/g, '');
        const numericValue = Number(normalizedValue || 0);
        const quantity = Math.min(Math.max(numericValue, 0), max);
        setInteractionQuantities((current) => ({
            ...current,
            [interaction.id]: quantity,
        }));
        setBondaCurrencyTotal(null);
    };

    const getFactorForScore = (score) => {
        const numericScore = Number(score || 0);
        return conversionFactors.find((factor) => (
            numericScore >= Number(factor.min) && numericScore <= Number(factor.max)
        ));
    };

    const calculateBondaCurrencyTotal = () => {
        return availableInteractions.reduce((total, interaction) => {
            const quantity = Number(interactionQuantities?.[interaction.id] || 0);
            const points = Number(interaction?.value || 0);
            if (quantity <= 0 || points <= 0) return total;
            const factor = getFactorForScore(points);
            if (!factor) return total;
            return total + (((points * Number(factor.factor || 0)) / 3) * quantity);
        }, 0);
    };

    const getSelectedInteractions = () => (
        availableInteractions
            .map((interaction) => ({
                id: interaction.id,
                quantity: Number(interactionQuantities?.[interaction.id] || 0),
            }))
            .filter((interaction) => interaction.quantity > 0)
    );

    const applyRedeemedInteractions = (selectedInteractions) => {
        const redeemedById = selectedInteractions.reduce((acc, interaction) => ({
            ...acc,
            [interaction.id]: interaction.quantity,
        }), {});

        setAvailableInteractions((current) => (
            current
                .map((interaction) => {
                    const quantity = Number(redeemedById[interaction.id] || 0);
                    if (quantity <= 0) return interaction;
                    const itemsAvailable = Math.max(Number(interaction.itemsAvailable || 0) - quantity, 0);
                    const itemsSpend = Number(interaction.itemsSpend || 0) + quantity;
                    return {
                        ...interaction,
                        itemsAvailable,
                        itemsSpend,
                        itemsToExchange: Math.max(itemsAvailable - itemsSpend, 0),
                    };
                })
                .filter((interaction) => Number(interaction.itemsToExchange || 0) > 0)
        ));
        setInteractionQuantities({});
    };

    const finishExchange = async () => {
        const lostAccess = await props.losesAccess?.();
        if (lostAccess) return;
        if (isPrizeUnavailable()) {
            props.message(redeemedPrizeMessage, 'error');
            setShowInteractionSelection(false);
            return;
        }
        const selectedInteractions = getSelectedInteractions();
        if (!selectedInteractions.length) return;
        const selectedScore = Math.round(Number(selectedInteractionsTotal || 0));
        if (selectedScore <= 0) return;
        const total = calculateBondaCurrencyTotal();
        if (isBondaPrize() && total <= 0) return;
        setAssigningBondaCurrency(true);
        try {
            const result = isBondaPrize()
                ? await BondaWalletService.assign(
                    Number(total.toFixed(2)),
                    selectedInteractions,
                    premioSeleccionado?.id,
                    selectedScore,
                )
                : await ExchangeHistoryService.complete(
                    premioSeleccionado?.id,
                    selectedScore,
                    selectedInteractions,
            );
            if (props.logout(result)) return;
            applyRedeemedInteractions(selectedInteractions);
            if (isUcTripPrize()) {
                setPrizes((current) => current.map((prize) => (
                    prize.id === premioSeleccionado?.id ? { ...prize, redeemed: true } : prize
                )));
            }
            setBondaCurrencyTotal(isBondaPrize() ? total : null);
            setPremioSeleccionado(null);
            setAceptarTerminos(false);
            setShowInteractionSelection(false);
            props.message(
                isBondaPrize()
                    ? `Canje realizado exitosamente. Valor canjeado en Bonda: ${Number(total).toFixed(2)}`
                    : 'Canje realizado exitosamente.',
                'success'
            );
        } catch (e) {
            props.message(e?.message || e, "error");
        } finally {
            setAssigningBondaCurrency(false);
        }
    };


    const handleViewTerms = async () => {
        const lostAccess = await props.losesAccess?.();
        if (lostAccess) return;
        try {
            const file = await FileService.getTermsFile();
            if (props.logout(file)) return;
            const url = URL.createObjectURL(file);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (e) {
            props.message(e?.message || e, "error");
        }
    };

    const volver = async () => {
        const lostAccess = await props.losesAccess?.();
        if (lostAccess) return;
        props.setProfile('perfil');
    };

    const volverAlCanje = async () => {
        const lostAccess = await props.losesAccess?.();
        if (lostAccess) return;
        setShowInteractionSelection(false);
    };

    const goToConfirmation = async () => {
        if (!premioSeleccionado) {
            props.message('Debes seleccionar un premio', 'error');
            return;
        }

        if (selectedInteractionsTotal <= 0) {
            props.message('Debes seleccionar interacciones a canjear', 'error');
            return;
        }

        const lostAccess = await props.losesAccess?.();
        if (lostAccess) return;

        if (isPrizeUnavailable()) {
            props.message(redeemedPrizeMessage, 'error');
            return;
        }

        const selectionError = getSelectionError();
        if (selectionError) {
            props.message(selectionError, 'error');
            return;
        }

        const factors = isBondaPrize() ? await ConversionFactorService.getAll() : [];
        if (factors && factors.length > 0) {
            setConversionFactors(factors);
        }
        setShowInteractionSelection(true);
    };

    return {
        prizes,
        country,
        userScore,
        aceptarTerminos,
        premioSeleccionado,
        showInteractionSelection,
        availableInteractions,
        interactionQuantities,
        selectedInteractionsTotal,
        selectionError,
        leftoverScore,
        bondaCurrencyTotal,
        assigningBondaCurrency,
        loadingInteractions,
        volver,
        volverAlCanje,
        goToConfirmation,
        canSubmit,
        hasEnoughPoints,
        isBondaPrize,
        isPrizeUnavailable,
        redeemedPrizeMessage,
        setInteractionQuantity,
        finishExchange,
        setAceptarTerminos,
        handleViewTerms,
        handlePremioSelect
    };
};

export default PrizesExchange;
